/** @jsx jsx */
import { Colors } from '/styles/variables';
import { css, jsx } from '@emotion/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { get } from 'srs-middleware';
import { Button, Card, Collapse, H5, Icon } from '@blueprintjs/core';
import { countBy, findIndex, groupBy } from 'lodash';

export interface SRSStatusProps {
	sortBy?(a : StatusGroup, b : StatusGroup): number;
	dilId: string;
}

interface StatusItem {
	title: string;
	message?: string;
	state: string;
	group?: string;
}

interface StatusGroup {
	name: string;
	items: StatusItem[];
	stateCounts: Record<string, number>;
	isUngroupedItem: boolean;
}

export const SRSStatus : React.FunctionComponent<SRSStatusProps> = ({
	sortBy, dilId
}) => {
	const [statusGroups, setStatusGroups] = useState<StatusGroup[]>([]);
	const [expandedGroups, setExpandedGroups] = useState<String[]>([]);

	const allGroupsExpanded = useMemo(
		() => expandedGroups.length === statusGroups.filter(g => !g.isUngroupedItem).length,
		[expandedGroups.length, statusGroups]
	);

	const hasAnyExpandableGroups = useMemo(
		() => statusGroups.filter(g => !g.isUngroupedItem).length > 0,
		[statusGroups]
	);
	
	const toggleOne = useCallback((groupName) => {
		setExpandedGroups((expandState) => {
			expandState = expandState.slice(0);
			if (expandState.includes(groupName)) {
				return expandState.filter((x) => x !== groupName);
			} else {
				expandState.push(groupName);
				return expandState;
			}
		});
	}, [setExpandedGroups]);

	const toggleAll = useCallback(() => {
		if (allGroupsExpanded) {
			setExpandedGroups([]);
		} else {
			setExpandedGroups(statusGroups.filter(g => !g.isUngroupedItem).map(g => g.name) || []);
		}
	}, [allGroupsExpanded, statusGroups])

	useEffect(() => {
		get(`/SRS/Application/SlyKit/DataValidation/${dilId}`).then((response : StatusItem[]) => {

			// assign any items without a group to their own unique group
			response = response.map((o, i) => {
				if (!o.hasOwnProperty("group"))
					o.group = defaultGroupKey + i;
				return o;
			});

			// group the validation results by group key, then count the number of results of each state in each group
			const statusGroups : StatusGroup[] = Object.entries(
					groupBy(response, o => o.group)
				)
				.map(group => ({
					name: group[0],
					items: group[1],
					isUngroupedItem: group[0].startsWith(defaultGroupKey),
					stateCounts: countBy(group[1], 'state')
				}))
				.sort(sortBy ??
					((a,b) => {
						// sort the groups according to their position in the original response
						// since sort order may have been lost during conversion to object in groupBy
						return findIndex(response, o => o.group == a.name) - findIndex(response, o => o.group == b.name);
					})
				);
			setStatusGroups(statusGroups);
		})
	}, []);

	return (
		<div css={wrapper}>
			{hasAnyExpandableGroups &&
				<Button
					icon={allGroupsExpanded ? 'collapse-all' : 'expand-all'}
					text={allGroupsExpanded ? 'Collapse All' : 'Expand All'}
					onClick={toggleAll}
					minimal
					small
					css={css`
						margin-bottom: 8px;
					`}
				/>
			}
			{statusGroups.map((group, i) =>
				<div css={groupWrapper} key={i}>
				{
					group.isUngroupedItem ? (
						<StatusItem 
							title={group.items[0].title}
							message={group.items[0].message}
							state={group.items[0].state}
						/>
					) : (
						<StatusGroup
							name={group.name}
							items={group.items}
							stateCounts={group.stateCounts}
							onClick={() => toggleOne(group.name)}
							isOpen={expandedGroups.includes(group.name)}
						/>
					)
				}
				</div>
			)}
		</div>
	)
}

const StatusGroup = ({name, items, stateCounts, onClick, isOpen}) => {
	return (
		<React.Fragment>
			<Card css={card} onClick={onClick} interactive={true}>
				<H5>{name}</H5>
				<div css={iconList}>
					{['Pass','Fail','Warn'].map((state, i) => {
						if (stateCounts[state]) {
							return (
								<div key={i}>
									<span>{stateCounts[state]}</span>
									<Icon css={() => css`color: ${statusStyleMap[state].iconColor}`} icon={statusStyleMap[state].iconName}/>
								</div>
							)
						}
					})}
				</div>
			</Card>
			<Collapse isOpen={isOpen}>
				{items.map((item, j) =>
					<StatusItem
						key={j}
						title={item.title}
						message={item.message}
						state={item.state} />
				)}
			</Collapse>
		</React.Fragment>
	)
}

const StatusItem = ({title, message, state}) => {
	return (
		<Card css={[card, css`background-color: ${statusStyleMap[state].background}`]}>
			<div>
				<p><i>{title}</i></p>
				{message && <p><b>{message}</b></p> }
			</div>
			<Icon css={() => css`color: ${statusStyleMap[state].iconColor}`} icon={statusStyleMap[state].iconName}/>
		</Card>
	)
}

const defaultGroupKey = '__default__';
// TODO: replace the hardcoded colors with refs to predefined colors
// once the predefined colors have better background colors
const statusStyleMap = {
	'Pass': {
		iconName: 'tick-circle',
		iconColor: Colors.FOREST2,
		background: Colors.LIGHT_GRAY5
	},
	'Fail': {
		iconName: 'error',
		iconColor: Colors.RED3,
		background: 'rgb(255,240,240)'
	},
	'Warn': {
		iconName: 'warning-sign',
		iconColor: Colors.GOLD4,
        background: 'rgb(255,251,229)'
	}
};

const wrapper = css`
	flex: 1 1;
	height: 100%;
	padding: 8px;
	background-color: whitesmoke;
	overflow: auto;
`;

const groupWrapper = css`
	margin-bottom: 10px;
`

const card = css`
	border-radius: 0;
	padding: 16px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border: 4px solid transparent;

	p, h5 {
		padding: 0 20px 0 0;
		margin: 0;
	}
`;

const iconList = css`
	display: flex;
	flex-direction: row;

	div {
		margin-left: 8px;
		white-space: nowrap;
	}

	& span {
		margin-left: 4px;
	}
`;